import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import { IApiResponse } from '@nomoh-monorepo/shared-types';
import { catchError, from, map, switchMap, throwError } from 'rxjs';
import { AuthDataService } from '../services/auth.dataservice.service';
import { PUBLIC_ROUTES } from '../tokens/auth-redirect.token';
import { estaNaRotaMinhaConta } from '../utils/minha-conta-retorno.util';

// Flag para evitar múltiplos refresh simultâneos
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authDataService = inject(AuthDataService);
  const router = inject(Router);
  const notificacao = inject(NotificacaoService);
  const publicRoutes = inject(PUBLIC_ROUTES);

  const isPublicRoute = () => {
    const currentPath = window.location.pathname;
    return publicRoutes.some(
      (route) => currentPath === route || currentPath.startsWith(route + '/')
    );
  };

  return next(req).pipe(
    // Unwrap IApiResponse envelope para sucesso
    map((event) => {
      if (event instanceof HttpResponse) {
        const body = event.body as IApiResponse<unknown>;
        if (body && typeof body === 'object' && 'viewScreen' in body) {
          if (body.viewScreen && body.message) {
            notificacao.notificacaoHttpSuccess(body.message);
          }
          return event.clone({ body: body.data });
        }
      }
      return event;
    }),
    catchError((error) => {
      const apiError = error?.error as IApiResponse<null> | null;

      // A mensagem de erro agora vem traduzida do backend.
      const message =
        apiError?.message ||
        error?.error?.message ||
        'Verifique sua conexão e tente novamente.';

      // ✅ Tratamento especial para erro 401 (sessão expirada)
      if (error.status === 401) {
        // Erros 401 do login = credenciais inválidas, não sessão expirada → mostra mensagem
        // (cobre tanto /auth/login quanto /auth/bardo/login)
        if (req.url.includes('/login')) {
          notificacao.notificacaoHttpError(message, 8000);
          return throwError(() => error);
        }

        // Não tenta refresh se a requisição já é refresh-token (evita loop)
        // (cobre tanto /auth/refresh-token quanto /auth/bardo/refresh-token)
        if (req.url.includes('/refresh-token')) {
          authDataService.clearAuth();

          const currentUrl = window.location.pathname;
          if (!currentUrl.includes('/login') && !isPublicRoute()) {
            const queryString = window.location.search;
            const targetUrl = queryString ? `/login${queryString}` : '/login';
            router.navigateByUrl(targetUrl);
            notificacao.notificacaoHttpError(
              'Sessão expirada. Faça login novamente.',
              7000
            );
          }

          return throwError(() => error);
        }

        // Se já está refreshing, aguarda o refresh em andamento
        if (isRefreshing && refreshPromise) {
          return from(refreshPromise).pipe(
            switchMap((success) => {
              if (success) {
                // ✅ CRÍTICO: Clone a requisição para forçar re-anexar os novos cookies
                const retryReq = req.clone({
                  withCredentials: true,
                });
                return next(retryReq);
              } else {
                // Refresh falhou, propaga erro
                return throwError(() => error);
              }
            })
          );
        }

        // Inicia processo de refresh
        isRefreshing = true;
        refreshPromise = authDataService.refreshToken().finally(() => {
          isRefreshing = false;
          refreshPromise = null;
        });

        return from(refreshPromise).pipe(
          switchMap((success) => {
            if (success) {
              // ✅ CRÍTICO: Clone a requisição para forçar re-anexar os novos cookies
              const retryReq = req.clone({
                withCredentials: true,
              });
              return next(retryReq);
            } else {
              authDataService.clearAuth();

              const currentUrl = window.location.pathname;
              if (!currentUrl.includes('/login') && !isPublicRoute()) {
                const queryString = window.location.search;
                const targetUrl = queryString
                  ? `/login${queryString}`
                  : '/login';
                router.navigateByUrl(targetUrl);
                notificacao.notificacaoHttpError(
                  'Sessão expirada. Faça login novamente.',
                  7000
                );
              }

              return throwError(() => error);
            }
          })
        );
      }

      if (req.url.includes('whatsapp/instance')) {
        return throwError(() => error);
      }

      // Erros estruturados (PLAN_EXPIRED, NO_PLAN) são tratados pelo AuthDataService
      const errorCode = apiError?.code || error?.error?.code;
      if (
        errorCode === 'PLAN_EXPIRED' ||
        errorCode === 'PLAN_CANCELLED' ||
        errorCode === 'NO_PLAN'
      ) {
        const isAuthFlow =
          req.url.includes('/login') || req.url.includes('/selecionar-contexto');
        if (!isAuthFlow && !estaNaRotaMinhaConta()) {
          if (!authDataService.redirecionarSePlanoBloqueado()) {
            router.navigate(['/plano-expirado'], {
              queryParams: {
                sistema: error?.error?.sistemaNome,
                sistemaId: error?.error?.sistemaId,
              },
            });
          }
        }
        return throwError(() => error);
      }

      // Demais erros de login (ex: 403 sem plano/permissão) sempre notificam,
      // mesmo com viewScreen=false, pois o formulário de login não exibe erro próprio.
      if (req.url.includes('/login') && message) {
        notificacao.notificacaoHttpWarn(message, 8000);
        return throwError(() => error);
      }

      // Silencia se viewScreen=false (backend não quer notificação)
      if (
        apiError &&
        'viewScreen' in apiError &&
        apiError.viewScreen === false
      ) {
        return throwError(() => error);
      }

      const status = error.status as number | undefined;
      const isValidationOrBusinessError =
        status === 400 ||
        status === 404 ||
        status === 409 ||
        status === 422;

      // Validação de campos (ValidationPipe) → aviso
      if (apiError?.errors?.length) {
        apiError.errors.forEach((err) =>
          notificacao.notificacaoHttpWarn(err, 8000)
        );
        return throwError(() => error);
      }

      // Regras de negócio / conflitos 4xx → aviso
      if (isValidationOrBusinessError && message) {
        notificacao.notificacaoHttpWarn(message, 8000);
        return throwError(() => error);
      }

      // Falha técnica, rede ou 5xx → erro
      notificacao.notificacaoHttpError(message, 8000);
      return throwError(() => error);
    })
  );
};
